/**
 * Overview tab's "Monthly Absence Volume" bar chart — total absence days per
 * month, split by Sick Leave / Child Sick Leave, with an optional prior-year
 * comparison stack.
 *
 * Split into its own file (out of AbsenceInsightsCharts.tsx) to keep that
 * file under the repo's max-lines lint limit, same reasoning as
 * DayOfWeekChart.tsx and AbsenceOrgTrendChart.tsx.
 */
import { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import type { AbsenceMonthlyPoint, AbsenceType } from '../services/absenceApi';
import { SICK_LEAVE_COLOR, CHILD_CARE_COLOR, pickDays, TogglePill, EmptyChartCard } from './AbsenceInsightsCharts';
import { InfoTooltip } from './InfoTooltip';

// Same muted greys used for "not the highlighted bar" elsewhere — duplicated
// rather than exported from AbsenceInsightsCharts.tsx, matching the existing
// precedent in DayOfWeekChart.tsx.
const MUTED_DARK = '#D7D7D7'; // pewter
const MUTED_LIGHT = '#F3F3F3'; // cotton

export function MonthlyVolumeChart({
  points,
  priorPoints,
  absenceType,
}: {
  points: AbsenceMonthlyPoint[];
  priorPoints: AbsenceMonthlyPoint[];
  absenceType: AbsenceType;
}) {
  const [showPrior, setShowPrior] = useState(false);

  if (points.length === 0) {
    return <EmptyChartCard title="Monthly Absence Volume" message="No absence data in the selected window." />;
  }

  const showSl = absenceType !== 'csl';
  const showCsl = absenceType !== 'sl';

  // Peak month(s) — determined off whichever metric the Absence Type filter
  // currently shows (so it's the SL-only peak when filtered to Sick Leave,
  // not always the combined total), tolerant of ties like the prototype's
  // own `t >= maxM - 0.01` check. A suppressed month's real total is unknown,
  // so it's treated as 0 here — it can never be highlighted as the peak.
  const totals = points.map((p) => pickDays(p, absenceType) ?? 0);
  const maxTotal = Math.max(...totals, 0);
  const isPeak = totals.map((t) => t >= maxTotal - 0.01 && maxTotal > 0);

  // Aligned by calendar month number against the CURRENT month axis (prior
  // data is a different year), same approach as the other YoY charts.
  const priorByMonth = new Map(priorPoints.map((p) => [p.month, p]));

  // Prior-year peak — computed off the prior year's own data (independent of
  // the current year's peak, which can land on a different month), so
  // toggling "Compare last year" shows which month was highest THAT year too.
  const priorTotal = (month: number) => {
    const p = priorByMonth.get(month);
    return p ? pickDays(p, absenceType) ?? 0 : 0;
  };
  const maxPriorTotal = Math.max(...priorPoints.map((p) => pickDays(p, absenceType) ?? 0), 0);
  const isPriorPeak = points.map((p) => priorByMonth.has(p.month) && priorTotal(p.month) >= maxPriorTotal - 0.01 && maxPriorTotal > 0);

  // Suppressed points' day-count fields are already null from the API — bars
  // with a null value simply don't render, so no separate leak-prevention
  // logic is needed here the way the rate charts need tooltip overrides.
  const datasets: import('chart.js').ChartDataset<'bar'>[] = [];
  if (showSl) {
    datasets.push({
      label: 'Sick Leave',
      data: points.map((p) => p.sickLeaveDays),
      backgroundColor: isPeak.map((peak) => (peak ? SICK_LEAVE_COLOR : MUTED_DARK)),
      borderRadius: 4,
      stack: 'days',
    });
  }
  if (showCsl) {
    datasets.push({
      label: 'Child Sick Leave',
      data: points.map((p) => p.childCareDays),
      backgroundColor: isPeak.map((peak) => (peak ? CHILD_CARE_COLOR : MUTED_LIGHT)),
      borderRadius: 4,
      stack: 'days',
    });
  }
  if (showPrior) {
    if (showSl) {
      datasets.push({
        label: 'Sick Leave (last year)',
        // 0 only when the prior month genuinely doesn't exist — a suppressed
        // month keeps its null rather than being coerced into a false "0".
        data: points.map((p) => {
          const prior = priorByMonth.get(p.month);
          return prior ? prior.sickLeaveDays : 0;
        }),
        backgroundColor: isPriorPeak.map((peak) => (peak ? CHILD_CARE_COLOR : MUTED_DARK)),
        borderRadius: 4,
        stack: 'prior',
      });
    }
    if (showCsl) {
      datasets.push({
        label: 'Child Sick Leave (last year)',
        data: points.map((p) => {
          const prior = priorByMonth.get(p.month);
          return prior ? prior.childCareDays : 0;
        }),
        backgroundColor: isPriorPeak.map((peak) => (peak ? CHILD_CARE_COLOR : MUTED_LIGHT)),
        borderRadius: 4,
        stack: 'prior',
      });
    }
  }

  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
            Monthly Absence Volume
            <InfoTooltip text="Total absence days per month, split by Sick Leave and Child Sick Leave. Raw day counts, not a rate — a busier-looking month can mean more people were out, or the same people were out longer." />
          </h2>
          <p className="mt-1 text-xs text-zinc-400">Total absence days per month</p>
        </div>
        <TogglePill active={showPrior} onClick={() => setShowPrior((v) => !v)}>
          Compare previous year
        </TogglePill>
      </div>
      <div className="mt-4" style={{ height: 220 }}>
        <Bar
          data={{ labels: points.map((p) => p.monthLabel), datasets }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: { boxWidth: 10, filter: (item) => !item.text.includes('(last year)') },
              },
            },
            scales: {
              x: { stacked: true },
              y: { stacked: true, beginAtZero: true },
            },
          }}
        />
      </div>
    </section>
  );
}
